// eo-map.ts — Phase 207 (MAPS-04): skeletal confirmed-data tile render for /eo/maps/<id>.
// Mirrors stardew-map.ts's SHAPE (page-marker guard, JSON.parse payload, panzoom
// init-once, mouse-only pointermove tooltip, CSS-px→canvas-px→cell coordinate math)
// while shedding every sprite/tint/head-canvas/async concern — this module only ever
// draws a theme-aware checkerboard base + flat-color rects/markers (D-01). init() is
// fully synchronous; there is no sprite bootstrap to await.
import Panzoom from '@panzoom/panzoom';

// ── Constants (frozen per 207-02-PLAN.md's <frozen_payload_contract>) ─────────────
// CELL_PX must match tools/probe-207-map-render.mjs (Plan 03). All four are
// UAT-tunable per CONTEXT.md Discretion, but must not silently drift from the plan.
const CELL_PX = 16;                    // px per tile
const ABSURD_MAX = 200;                // defensive width/height clamp (observed max ~86x86, 206 byte-trace)
const TAP_MOVEMENT_THRESHOLD_PX = 10;  // CSS px — RESEARCH.md Code Examples tap-vs-drag detector
const TOOLTIP_OFFSET_X = 12;
const TOOLTIP_OFFSET_Y = 12;

// ── Payload contract (server: MapsModel::getMapRenderPayload(), Plan 01/207-01) ───
// EXACT keys — do NOT invent eo_npc_id/target_map_id (those are DB column names,
// not payload keys).
interface MapSpawn {
    id: number;             // = npc id
    name: string;
    amount: number | null;
    x: number | null;
    y: number | null;
}

interface MapWarp {
    target_id: number;
    target_name: string;
    locked: boolean;
    level_req: number | null;
    x: number | null;
    // NO y — D-08: EmfFile.php hardcodes warp source-Y to 0 (withheld, not real data).
    // Never draw/surface it as a precise row-0 cell.
}

interface MapPayload {
    width: number;
    height: number;
    spawns: MapSpawn[];
    warps: MapWarp[];
}

// ── Hit-testing (shared by wireTooltip + wireTapNavigate) ─────────────────────────
// Priority order: spawns (precise x/y) checked before warps (column x only), matching
// D-04's "hover/tap a block" contract and RESEARCH.md's Code Example ordering.

function hitTestSpawn(payload: MapPayload, cellX: number, cellY: number): MapSpawn | null {
    for (const s of payload.spawns) {
        if (s.x === null || s.y === null) continue;   // guard null coords (D-05 defensive)
        if (s.x === cellX && s.y === cellY) return s;
    }
    return null;
}

function hitTestWarp(payload: MapPayload, cellX: number): MapWarp | null {
    for (const w of payload.warps) {
        if (w.x === null) continue;                   // guard null coords
        if (w.x === cellX) return w;
    }
    return null;
}

// ── drawMap(): full repaint from the payload ───────────────────────────────────────
function drawMap(canvas: HTMLCanvasElement, payload: MapPayload): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cols = Math.min(payload.width, ABSURD_MAX);
    const rows = Math.min(payload.height, ABSURD_MAX);
    const canvasW = cols * CELL_PX;
    const canvasH = rows * CELL_PX;

    // Assigning canvas.width/height RESETS context state per the HTML Living Standard
    // (clears bitmap, resets imageSmoothingEnabled/fillStyle/etc) — size FIRST, THEN
    // set context defaults (Pitfall 2 — a previously-fixed bug in stardew-map.ts's history).
    canvas.width  = canvasW;
    canvas.height = canvasH;
    ctx.imageSmoothingEnabled = false;

    // Theme-aware checkerboard base (D-07/D-CARRY) — resolved at draw time (not cached
    // at module load) so a light/dark theme toggle repaints correctly on next drawMap()
    // call. Idiom source: home.ts:263 / lastfm.ts:77,135.
    const shadeA = getComputedStyle(document.documentElement).getPropertyValue('--ksat-surface-card').trim();
    const shadeB = getComputedStyle(document.documentElement).getPropertyValue('--ksat-surface-raised').trim();
    for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
            ctx.fillStyle = (x + y) % 2 === 0 ? shadeA : shadeB;
            ctx.fillRect(x * CELL_PX, y * CELL_PX, CELL_PX, CELL_PX);
        }
    }

    // Palette-deviation note (D-CARRY, disclosed in 207-02-PLAN.md objective): no
    // --ksat-danger/--ksat-success tokens exist in src/css/tokens.scss — spawn/warp
    // markers resolve from Bootstrap's own runtime custom properties instead, which
    // also flip under data-bs-theme and so remain theme-aware.
    const spawnColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-danger').trim();
    const warpColor  = getComputedStyle(document.documentElement).getPropertyValue('--bs-success').trim();

    // Mob spawns (D-02) — flat red-ish blocks at precise x/y.
    ctx.fillStyle = spawnColor;
    for (const s of payload.spawns) {
        if (s.x === null || s.y === null) continue;
        ctx.fillRect(s.x * CELL_PX, s.y * CELL_PX, CELL_PX, CELL_PX);
    }

    // Warps (D-08/Pitfall 4) — DISTINCT green-ish column marker keyed on x ONLY.
    // Warp source-Y is a withheld decoder placeholder (always 0) — NEVER draw a
    // filled (x,0) cell, which would falsely imply precise row data. Instead: a
    // full-height translucent column stripe + a small top-edge chevron.
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = warpColor;
    for (const w of payload.warps) {
        if (w.x === null) continue;
        ctx.fillRect(w.x * CELL_PX, 0, CELL_PX, canvasH);
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = warpColor;
    for (const w of payload.warps) {
        if (w.x === null) continue;
        const cx = w.x * CELL_PX + CELL_PX / 2;
        ctx.beginPath();
        ctx.moveTo(cx - CELL_PX / 2, 0);
        ctx.lineTo(cx + CELL_PX / 2, 0);
        ctx.lineTo(cx, CELL_PX / 2);
        ctx.closePath();
        ctx.fill();
    }
}

// ── wireTooltip(): mouse-only hover, textContent-only (T-207-02) ──────────────────
function wireTooltip(canvas: HTMLCanvasElement, tooltip: HTMLElement, payload: MapPayload): void {
    canvas.addEventListener('pointermove', (e: PointerEvent) => {
        if (e.pointerType !== 'mouse') return;   // D-04: tooltip is mouse-only

        // CSS-px → canvas-px → cell coordinate conversion (stardew-map.ts:1344-1350 idiom).
        const rect    = canvas.getBoundingClientRect();
        const cssX    = e.clientX - rect.left;
        const cssY    = e.clientY - rect.top;
        const canvasX = cssX * (canvas.width  / rect.width);
        const canvasY = cssY * (canvas.height / rect.height);
        const cellX   = Math.floor(canvasX / CELL_PX);
        const cellY   = Math.floor(canvasY / CELL_PX);

        const spawnHit = hitTestSpawn(payload, cellX, cellY);
        if (spawnHit) {
            tooltip.textContent = spawnHit.amount !== null
                ? `${spawnHit.name} (x${spawnHit.amount})`
                : spawnHit.name;
            tooltip.style.left = `${e.clientX + TOOLTIP_OFFSET_X}px`;
            tooltip.style.top  = `${e.clientY + TOOLTIP_OFFSET_Y}px`;
            tooltip.hidden = false;
            return;
        }

        const warpHit = hitTestWarp(payload, cellX);
        if (warpHit) {
            tooltip.textContent = `Warp: ${warpHit.target_name}`;
            tooltip.style.left = `${e.clientX + TOOLTIP_OFFSET_X}px`;
            tooltip.style.top  = `${e.clientY + TOOLTIP_OFFSET_Y}px`;
            tooltip.hidden = false;
            return;
        }

        tooltip.hidden = true;
    });

    canvas.addEventListener('mouseleave', () => {
        tooltip.hidden = true;
    });
}

// ── wireTapNavigate(): movement-threshold tap-vs-drag detector ────────────────────
// No literal in-repo precedent (Pitfall 1 — stardew-map.ts has zero click/tap
// handlers on its canvas). Built fresh from RESEARCH.md's Code Examples.
function wireTapNavigate(canvas: HTMLCanvasElement, payload: MapPayload): void {
    let tapStart: { x: number; y: number; id: number } | null = null;

    canvas.addEventListener('pointerdown', (e: PointerEvent) => {
        tapStart = { x: e.clientX, y: e.clientY, id: e.pointerId };
    });

    canvas.addEventListener('pointerup', (e: PointerEvent) => {
        if (!tapStart || tapStart.id !== e.pointerId) { tapStart = null; return; }
        const dx = e.clientX - tapStart.x;
        const dy = e.clientY - tapStart.y;
        const moved = Math.sqrt(dx * dx + dy * dy);
        tapStart = null;
        if (moved > TAP_MOVEMENT_THRESHOLD_PX) return;   // was a pan/drag, not a tap

        const rect  = canvas.getBoundingClientRect();
        const cellX = Math.floor((e.clientX - rect.left) * (canvas.width  / rect.width)  / CELL_PX);
        const cellY = Math.floor((e.clientY - rect.top)  * (canvas.height / rect.height) / CELL_PX);

        const spawnHit = hitTestSpawn(payload, cellX, cellY);
        if (spawnHit) {
            // Numeric id only — never interpolate name/target_name into the URL (T-207-03).
            window.location.href = '/eo/mobs/' + spawnHit.id;
            return;
        }
        const warpHit = hitTestWarp(payload, cellX);   // column-only per D-08
        if (warpHit) {
            window.location.href = '/eo/maps/' + warpHit.target_id;
        }
    });
}

// ── Module default export (mirrors src/index.ts's svFarmMap.init() convention) ────
export default {
    init(): void {
        // Page-marker guard: no-op on every page that does NOT have a [data-eo-map] canvas.
        const canvas = document.querySelector<HTMLCanvasElement>('[data-eo-map]');
        if (!canvas) return;

        const raw     = canvas.dataset.eoMap;
        const tooltip = document.querySelector<HTMLElement>('.eo-map-tooltip');
        if (!raw || !tooltip) return;

        // Narrow try/catch around JSON.parse ONLY — never the whole init body
        // (stardew-avatar.ts:945-958 convention). A malformed payload short-circuits
        // cleanly while real runtime bugs elsewhere still propagate to the console.
        let payload: MapPayload;
        try {
            payload = JSON.parse(raw) as MapPayload;
        } catch {
            // Skip silently on parse error.
            return;
        }

        drawMap(canvas, payload);
        wireTooltip(canvas, tooltip, payload);
        wireTapNavigate(canvas, payload);

        // Panzoom init-once (D-06: fit-to-width is the zoom floor; pinch-zoom + two-finger
        // pan on phone). Copied near-verbatim from stardew-map.ts:1540-1582 (207-PATTERNS.md
        // Pattern 2) — this single block satisfies the phone-safe/no-overflow contract.
        // Guard: NEVER init inside drawMap (would re-run on every repaint).
        if (canvas.dataset.panzoomInit !== '1') {
            canvas.dataset.panzoomInit = '1';
            const wrap = canvas.parentElement as HTMLElement;   // .eo-map-wrap (gesture viewport)

            // Guard a zero-width rect (display:none ancestor / detached) so fitScale can't
            // be Infinity; clamp the ceiling to a finite 20x so maxScale is always sane.
            const rectW    = canvas.getBoundingClientRect().width;
            const fitScale = rectW > 0 ? canvas.width / rectW : 5;

            const pz = Panzoom(canvas, {
                noBind: true,             // manual bind below filters mouse
                canvas: true,             // redirects Panzoom's cursor write to the parent (wrap)
                                          // so the SCSS .eo-map-canvas crosshair survives
                minScale: 1,              // D-06: fitted overview is the zoom floor
                maxScale: Math.min(20, Math.max(5, fitScale)),
                pinchAndPan: true,        // two-finger pan while zoomed
                panOnlyWhenZoomed: true,  // rest state is undraggable
                cursor: '',               // preserve SCSS crosshair contract
            });

            const isNotMouse = (e: PointerEvent) => e.pointerType !== 'mouse';
            wrap.addEventListener('pointerdown', (e) => { if (isNotMouse(e)) pz.handleDown(e); });
            document.addEventListener('pointermove', (e) => { if (isNotMouse(e)) pz.handleMove(e); }, { passive: true });
            document.addEventListener('pointerup',   (e) => { if (isNotMouse(e)) pz.handleUp(e);   }, { passive: true });
            // Mirrors Panzoom's own bind(): down on parent (canvas:true semantics), move/up on document, passive.

            // Snap-home on scale-reset (Pitfall 3) — { force: true } is LOAD-BEARING. Plain
            // pz.pan(0,0) routes through constrainXY, which returns {x,y} UNCHANGED when
            // panOnlyWhenZoomed && scale === startScale (=1) — exactly the zoom floor this
            // handler targets — so the re-center would be a silent no-op without force.
            canvas.addEventListener('panzoomend', () => {
                if (pz.getScale() <= 1.001) pz.pan(0, 0, { force: true });
            });
        }
    },
};
